import { observable, runInAction } from 'mobx'
import type {
  PrimeProviderFn,
  PrimeUserFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import { Provider } from '@/features/iam/providers/Provider'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { maybeParseDate, safeParseDate } from '@/utils/date-util'
import { type AdminIamApi } from '@/features/admin/api/admin-iam-api'
import {
  type UpdateWorkspacePostingRequest,
  type WorkspacePostingJobTypeResource,
  type WorkspacePostingResource,
  type WorkspacePostingStatusResource,
} from '@/features/admin/api/resources/workspace-posting'
import { type WorkspaceApplicantResource } from '@/features/admin/api/resources/workspace-applicant'
import {
  WorkspacePosting,
  type WorkspacePostingJobType,
  type WorkspacePostingOpportunityType,
  type WorkspacePostingProps,
  type WorkspacePostingStatus,
} from '@/features/admin/staffing/WorkspacePosting'
import { type WorkspaceApplicantProps } from '@/features/admin/staffing/WorkspaceApplicant'
import type {
  LocationOption,
  PrimarySoftwareOption,
  SpecialtyOption,
  SupplementalSoftwareOption,
} from '@/features/admin/staffing/workspace-posting-field-options'
import { type WorkspacePostingAggregation } from '@/features/admin/staffing/WorkspacePostingAggregation'
import {
  mapWorkspaceApplicantStatusResourceToStatus,
  mapWorkspacePostingAggregationResourceToAggregation,
  mapWorkspacePostingJobTypeResourceToJobType,
  mapWorkspacePostingJobTypeToResource,
  mapWorkspacePostingOpportunityTypeResourceToOpportunityType,
  mapWorkspacePostingOpportunityTypeToResource,
  mapWorkspacePostingStatusResourceToStatus,
  mapWorkspacePostingStatusToResource,
} from '@/features/admin/staffing/workspace-posting-mappers'

/**
 * Query for listing workspace postings.
 */
export interface ListWorkspacePostingsQuery extends CursorPageQuery {
  workspaceIds: Id[]
  statuses: WorkspacePostingStatus[]
}

/**
 * The editable details of a workspace posting.
 */
export interface WorkspacePostingDetails {
  location?: LocationOption
  primarySoftware?: PrimarySoftwareOption
  supplementalSoftware?: SupplementalSoftwareOption
  backupFileNeeded: boolean
  specialties: SpecialtyOption[]
  capacity: number
  maxAllowedWorkspaces: number
  jobTypes: WorkspacePostingJobType[]
  description: string
}

/**
 * Params for creating a workspace posting.
 */
export interface CreateWorkspacePostingParams extends WorkspacePostingDetails {
  opportunityType: WorkspacePostingOpportunityType
}

export class WorkspacePostingStore {
  /**
   * Workspace posting collection.
   */
  readonly workspacePostings = collection<
    WorkspacePosting,
    Id,
    WorkspacePostingProps
  >({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new WorkspacePosting(),
  })

  /**
   * Number of workspace postings per status, from the last aggregation fetch.
   */
  @observable.ref
  accessor workspacePostingAggregations: readonly WorkspacePostingAggregation[] =
    []

  constructor(
    private readonly _api: AdminIamApi,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _primeUser: PrimeUserFn,
    private readonly _primeProvider: PrimeProviderFn,
  ) {}

  /**
   * Lists workspace postings.
   */
  async listWorkspacePostings(
    query: ListWorkspacePostingsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<WorkspacePosting>> {
    const statuses = query.statuses
      .map(mapWorkspacePostingStatusToResource)
      .filter((s): s is WorkspacePostingStatusResource => s !== undefined)

    const response = await this._api.workspacePostings.list(
      {
        workspace_ids: query.workspaceIds.map(Id.assert),
        // Filtering by all five statuses is the same as not filtering.
        status: statuses.length === 5 ? [] : statuses,
        limit: query.limit,
        after: query.after,
      },
      signal,
    )

    return {
      next: response.cursor,
      data: this.workspacePostings.set(
        response.data.map((r) => this._mapWorkspacePostingResourceToProps(r)),
      ),
    }
  }

  /**
   * Fetches a single workspace posting.
   */
  async fetchWorkspacePosting(
    workspacePostingId: Id,
    signal?: AbortSignal,
  ): Promise<WorkspacePosting> {
    const updated = await this._api.workspacePostings.get(
      Id.assert(workspacePostingId),
      signal,
    )
    return this.workspacePostings.set(
      this._mapWorkspacePostingResourceToProps(updated),
    )
  }

  /**
   * Fetches the number of workspace postings per status.
   */
  async fetchAggregations(
    workspaceIds: Id[],
    signal?: AbortSignal,
  ): Promise<void> {
    const resources = await this._api.workspacePostings.getAggregation(
      { workspace_ids: workspaceIds.map(Id.assert) },
      signal,
    )
    runInAction(() => {
      this.workspacePostingAggregations = resources.map(
        mapWorkspacePostingAggregationResourceToAggregation,
      )
    })
  }

  /**
   * Create a workspace posting.
   * @param workspaceId
   * @param params
   */
  async createWorkspacePosting(
    workspaceId: Id,
    { opportunityType, ...details }: CreateWorkspacePostingParams,
  ): Promise<void> {
    const posting = await this._api.workspacePostings.create(
      Id.assert(workspaceId),
      {
        opportunity_type:
          mapWorkspacePostingOpportunityTypeToResource(opportunityType),
        ...this._mapWorkspacePostingDetailsToRequest(details),
      },
    )
    this.workspacePostings.set(
      this._mapWorkspacePostingResourceToProps(posting),
    )
  }

  /**
   * Update a workspace posting.
   * @param id
   * @param details
   */
  async updateWorkspacePosting(
    id: Id,
    details: WorkspacePostingDetails,
  ): Promise<void> {
    const posting = await this._api.workspacePostings.updateDraft(
      Id.assert(id),
      this._mapWorkspacePostingDetailsToRequest(details),
    )
    this.workspacePostings.set(
      this._mapWorkspacePostingResourceToProps(posting),
    )
  }

  /**
   * Publish a workspace posting.
   * @param id
   */
  async publishWorkspacePosting(id: Id): Promise<void> {
    await this._api.workspacePostings.publish(Id.assert(id))
  }

  /**
   * Reactivate an InReview workspace posting
   */
  async reactivatePosting(id: Id): Promise<void> {
    await this._api.workspacePostings.reactivate(Id.assert(id))
  }

  /**
   * Manually close an InReview workspace posting
   */
  async manuallyClose(id: Id): Promise<void> {
    await this._api.workspacePostings.manuallyClose(Id.assert(id))
  }

  /**
   * Mark a workspace posting as in review.
   * @param id
   */
  async markInReview(id: Id): Promise<void> {
    await this._api.workspacePostings.markInReview(Id.assert(id))
  }

  /**
   * Invite an applicant to participate in a workspace posting.
   * @param id
   * @param applicantPublicId
   */
  async inviteApplicant(id: Id, applicantPublicId: Id): Promise<void> {
    await this._api.workspacePostings.inviteApplicant(
      Id.assert(id),
      Id.assert(applicantPublicId),
    )
  }

  /**
   * Rescind an invitation to an applicant.
   * @param id
   * @param applicantPublicId
   */
  async rescindInvitation(id: Id, applicantPublicId: Id) {
    await this._api.workspacePostings.rescindInvitation(
      Id.assert(id),
      Id.assert(applicantPublicId),
    )
  }

  private _mapWorkspacePostingDetailsToRequest(
    details: WorkspacePostingDetails,
  ): UpdateWorkspacePostingRequest {
    return {
      location: details.location,
      primary_software: details.primarySoftware,
      backup_file_needed: details.backupFileNeeded,
      supplemental_software: details.supplementalSoftware,
      specialties: details.specialties,
      capacity: details.capacity,
      max_allowed_workspaces: details.maxAllowedWorkspaces,
      job_types: details.jobTypes
        .map(mapWorkspacePostingJobTypeToResource)
        .filter((t): t is WorkspacePostingJobTypeResource => t !== undefined),
      description: details.description,
    }
  }

  private _mapWorkspacePostingResourceToProps(
    resource: WorkspacePostingResource,
  ): WorkspacePostingProps {
    return {
      id: resource.id,
      workspaceId: resource.workspace_id,
      workspace: resource.workspace_id
        ? (this._primeWorkspace({ id: resource.workspace_id }) ??
          Workspace.none)
        : Workspace.none,
      dateCreated: safeParseDate(resource.date_created),
      dateModified: safeParseDate(resource.date_modified),
      location: resource.location,
      primarySoftware: resource.primary_software,
      supplementalSoftware: resource.supplemental_software,
      backupFileNeeded: resource.backup_file_needed,
      specialties: resource.specialties,
      capacity: resource.capacity,
      maxAllowedWorkspaces: resource.max_allowed_workspaces,
      jobTypes: resource.job_types.map(
        mapWorkspacePostingJobTypeResourceToJobType,
      ),
      description: resource.description,
      status: mapWorkspacePostingStatusResourceToStatus(resource.status),
      opportunityType:
        mapWorkspacePostingOpportunityTypeResourceToOpportunityType(
          resource.opportunity_type,
        ),
      applicants: resource.applicants.map((a) =>
        this._mapWorkspaceApplicantResourceToProps(a),
      ),
    }
  }

  private _mapWorkspaceApplicantResourceToProps(
    resource: WorkspaceApplicantResource,
  ): WorkspaceApplicantProps {
    return {
      user: this._primeUser({ publicId: resource.user_public_id }),
      provider:
        this._primeProvider({ userPublicId: resource.user_public_id }) ??
        Provider.none,
      userPublicId: Id(resource.user_public_id),
      status: mapWorkspaceApplicantStatusResourceToStatus(resource.status),
      dateCreated: safeParseDate(resource.date_created),
      dateModified: safeParseDate(resource.date_modified),
      dateInvited: maybeParseDate(resource.date_invited) ?? null,
    }
  }
}
